import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Save, X, Upload, AlertTriangle, Package, Zap, Settings, Truck, AlertCircle, Check } from 'lucide-react';
import { panelRequestAPI } from '../../services/api';
import MultiplePanelSelector from './MultiplePanelSelector';

interface PanelFormData {
  // Basic Information
  brand: string;
  model: string;
  description: string;
  
  // Power Specifications
  wattage: number;
  efficiency: number;
  technology: string;
  pmax: number;
  vmp: number;
  imp: number;
  voc: number;
  isc: number;
  
  // Physical Specifications
  dimensions: string;
  length: number;
  width: number;
  thickness: number;
  weight: number;
  numberOfCells: number;
  frameColor: string;
  
  // Electrical & Performance
  tempCoeffPmax: number;
  tempCoeffVoc: number;
  tempCoeffIsc: number;
  
  // Logistics
  panelsPerPallet: number;
  palletsPerTruck: number;
  snowLoad: number;
  windLoad: number;
  
  // Additional
  warranty: number;
  cableLength: number;
  connectorType: string;
  remarks: string;
}

interface RequestPanelFormProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

const RequestPanelForm: React.FC<RequestPanelFormProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<PanelFormData>({
    // Basic Information
    brand: '',
    model: '',
    description: '',
    
    // Power Specifications
    wattage: 0,
    efficiency: 0,
    technology: '',
    pmax: 0,
    vmp: 0,
    imp: 0,
    voc: 0,
    isc: 0,
    
    // Physical Specifications
    dimensions: '',
    length: 0,
    width: 0,
    thickness: 0,
    weight: 0,
    numberOfCells: 0,
    frameColor: '',
    
    // Electrical & Performance
    tempCoeffPmax: 0,
    tempCoeffVoc: 0,
    tempCoeffIsc: 0,
    
    // Logistics
    panelsPerPallet: 0,
    palletsPerTruck: 0,
    snowLoad: 0,
    windLoad: 0,
    
    // Additional
    warranty: 0,
    cableLength: 0,
    connectorType: '',
    remarks: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [existingPanels, setExistingPanels] = useState<any[]>([]);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [pdfParsing, setPdfParsing] = useState(false);
  const [parsingProgress, setParsingProgress] = useState('');
  const [multiplePanels, setMultiplePanels] = useState<any[]>([]);
  const [showMultipleSelector, setShowMultipleSelector] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const numericFields = [
      'wattage', 'efficiency', 'pmax', 'vmp', 'imp', 'voc', 'isc',
      'length', 'width', 'thickness', 'weight', 'numberOfCells',
      'tempCoeffPmax', 'tempCoeffVoc', 'tempCoeffIsc',
      'panelsPerPallet', 'palletsPerTruck', 'snowLoad', 'windLoad',
      'warranty', 'cableLength'
    ];
    
    setFormData((prev: any) => ({
      ...prev,
      [name]: numericFields.includes(name) ? Number(value) : value
    }));
  };

  const handlePdfUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('PDF upload triggered');
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, 'Type:', file.type, 'Size:', file.size);
    
    // Reset input so same file can be selected again
    e.target.value = '';
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      console.log('Invalid file type:', file.type);
      setError('Please select a PDF file for auto-parsing');
      return;
    }

    // Set attachment immediately and trigger parsing
    console.log('Setting attachment and starting parsing...');
    setAttachment(file);
    setError(null);
    handleParsePdf(file);
  };

  const handleRegularFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
      setAttachment(file);
      setError(null);
    } else {
      setError('Please upload only PDF or image files');
      e.target.value = '';
    }
  };

  const handleParsePdf = async (file: File) => {
    console.log('handleParsePdf called with file:', file.name);
    
    if (file.type !== 'application/pdf') {
      console.log('File type validation failed:', file.type);
      setError('Please select a PDF file to parse');
      return;
    }

    console.log('Starting PDF parsing process...');
    setPdfParsing(true);
    setError(null);
    setParsingProgress('Uploading PDF...');

    // Progress simulation since we can't track actual progress
    const progressSteps = [
      'Uploading PDF...',
      'Analyzing document structure...',
      'Extracting text content...',
      'Identifying panel specifications...',
      'Processing technical data...',
      'Finalizing extraction...'
    ];

    let progressIndex = 0;
    const progressInterval = setInterval(() => {
      if (progressIndex < progressSteps.length - 1) {
        progressIndex++;
        setParsingProgress(progressSteps[progressIndex]);
      }
    }, 15000); // Change message every 15 seconds

    try {
      console.log('Making API call to parse PDF...');
      const result = await panelRequestAPI.parsePdf(file);
      console.log('API call completed, result:', result);
      
      // Ensure all numeric fields are properly handled
      if (result.data) {
        Object.keys(result.data).forEach(key => {
          if (result.data[key] === null || result.data[key] === undefined) {
            result.data[key] = '';
          }
        });
      }
      clearInterval(progressInterval);
      
      if (result.success && result.data) {
        console.log('Parsed panels count:', result.panelCount);
        
        if (Array.isArray(result.data) && result.data.length > 1) {
          // Multiple panels found
          setMultiplePanels(result.data);
          setParsingProgress(`Successfully extracted ${result.data.length} panels!`);
          setShowMultipleSelector(true);
        } else {
          // Single panel or first panel of array
          const panelData = Array.isArray(result.data) ? result.data[0] : result.data;
          setFormData(panelData);
          setParsingProgress('Successfully extracted panel data!');
        }
        
        // Show duplicates warning if any
        if (result.duplicates && result.duplicates.length > 0) {
          setExistingPanels(result.duplicates);
          setShowDuplicateWarning(true);
        }
        
        setError(null);
        
        // Clear success message after 3 seconds if not showing multiple selector
        if (!Array.isArray(result.data) || result.data.length === 1) {
          setTimeout(() => setParsingProgress(''), 3000);
        }
      }
    } catch (err: any) {
      console.error('PDF parsing failed:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      clearInterval(progressInterval);
      setParsingProgress('');
      
      const errorMessage = err.response?.data?.message || 'Failed to parse PDF. This can happen if the PDF format is not supported or the content is unclear. Please fill the form manually.';
      setError(errorMessage);
    } finally {
      setPdfParsing(false);
    }
  };

  const handleMultiplePanelSubmission = async (selectedPanels: any[]) => {
    setLoading(true);
    setError(null);

    try {
      const result = await panelRequestAPI.submitMultiplePanelRequests(
        selectedPanels,
        attachment?.name || 'Unknown PDF',
        attachment || undefined
      );
      
      setSuccess(true);
      setShowMultipleSelector(false);
      setMultiplePanels([]);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Multiple panel submission failed:', err);
      setError(err.response?.data?.message || 'Failed to submit panel requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent, forceSubmit = false) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSubmit = new FormData();
      
      // Add panel data
      formDataToSubmit.append('panelData', JSON.stringify(formData));
      formDataToSubmit.append('description', formData.description || `Request for ${formData.brand} ${formData.model}`);
      formDataToSubmit.append('forceSubmit', forceSubmit.toString());
      
      // Add attachment if present
      if (attachment) {
        formDataToSubmit.append('attachment', attachment);
      }

      const response = await panelRequestAPI.requestPanel(formDataToSubmit);
      setSuccess(true);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        // Duplicate panels found
        setExistingPanels(err.response.data.existingPanels);
        setShowDuplicateWarning(true);
      } else {
        setError(err.response?.data?.message || 'Failed to submit panel request');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinueWithDuplicate = () => {
    setShowDuplicateWarning(false);
    handleSubmit(new Event('submit') as any, true);
  };

  const resetForm = () => {
    setFormData({
      brand: '',
      model: '',
      description: '',
      wattage: 0,
      efficiency: 0,
      technology: '',
      pmax: 0,
      vmp: 0,
      imp: 0,
      voc: 0,
      isc: 0,
      dimensions: '',
      length: 0,
      width: 0,
      thickness: 0,
      weight: 0,
      numberOfCells: 0,
      frameColor: '',
      tempCoeffPmax: 0,
      tempCoeffVoc: 0,
      tempCoeffIsc: 0,
      panelsPerPallet: 0,
      palletsPerTruck: 0,
      snowLoad: 0,
      windLoad: 0,
      warranty: 0,
      cableLength: 0,
      connectorType: '',
      remarks: ''
    });
    setAttachment(null);
    setError(null);
    setSuccess(false);
    setShowDuplicateWarning(false);
    setExistingPanels([]);
  };

  // Multiple panels selector modal
  if (showMultipleSelector) {
    return (
      <MultiplePanelSelector
        panels={multiplePanels}
        onSubmit={handleMultiplePanelSubmission}
        onCancel={() => {
          setShowMultipleSelector(false);
          setMultiplePanels([]);
          // Fill form with first panel as default
          if (multiplePanels.length > 0) {
            setFormData(multiplePanels[0]);
          }
        }}
        loading={loading}
        originalFileName={attachment?.name || 'PDF'}
      />
    );
  }

  // Duplicate warning modal
  if (showDuplicateWarning) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Similar Panels Found</h3>
            </div>
            <p className="text-gray-600 mb-4">
              We found similar panels in our database. Please review them before continuing:
            </p>
            
            <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
              {existingPanels.map((panel, index) => (
                <div key={index} className="border rounded-lg p-3 bg-gray-50">
                  <div className="font-medium text-gray-900">{panel.SKU}</div>
                  <div className="text-sm text-gray-600">{panel.Description}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {panel.Watts_W}W • {panel.ModuleEfficiency_percent}% • {panel.CellType}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDuplicateWarning(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleContinueWithDuplicate}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Continue Anyway'}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-md p-6"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted!</h3>
          <p className="text-gray-600 mb-6">Your panel request has been submitted successfully. An admin will review it shortly.</p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Submit Another Request
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-6 max-w-6xl mx-auto"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Request New Panel</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-8">
        {/* PDF Upload Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-dashed border-blue-200">
          <div className="text-center">
            <Upload className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Quick Upload from PDF</h3>
            <p className="text-gray-600 mb-4">
              Upload a solar panel datasheet PDF to automatically fill the form fields
            </p>
            
            <div className="space-y-4">
              <label className={`cursor-pointer inline-flex items-center justify-center px-6 py-3 rounded-lg transition-colors ${
                pdfParsing 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}>
                <Upload className="w-5 h-5 mr-2" />
                {pdfParsing ? 'Processing...' : 'Upload PDF Datasheet'}
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="hidden"
                  disabled={pdfParsing}
                />
              </label>
              
              {pdfParsing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-center mb-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-blue-700 font-medium">Processing PDF...</span>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-blue-600 mb-2">{parsingProgress}</p>
                    <p className="text-xs text-gray-500">
                      This may take 2-3 minutes. Please don't close this page.
                    </p>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2 mt-3">
                    <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                  </div>
                </div>
              )}
              
              {parsingProgress && !pdfParsing && parsingProgress.includes('Successfully') && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-green-700 font-medium">{parsingProgress}</span>
                  </div>
                </div>
              )}
              
              <p className="text-sm text-gray-500">
                Or fill the form manually below
              </p>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Package className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand *</label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., SunPower"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., SPR-MAX3-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Technology</label>
              <input
                type="text"
                name="technology"
                value={formData.technology}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Monocrystalline"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Detailed description of the panel..."
            />
          </div>
        </div>

        {/* Power Specifications */}
        <div className="bg-yellow-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Zap className="w-5 h-5 text-yellow-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Power Specifications</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Wattage (W) *</label>
              <input
                type="text"
                name="wattage"
                value={formData.wattage || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Efficiency (%)</label>
              <input
                type="text"
                name="efficiency"
                value={formData.efficiency || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="22.8"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pmax (W)</label>
              <input
                type="text"
                name="pmax"
                value={formData.pmax}
                onChange={handleInputChange}
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vmp (V)</label>
              <input
                type="text"
                name="vmp"
                value={formData.vmp}
                onChange={handleInputChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="32.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Imp (A)</label>
              <input
                type="text"
                name="imp"
                value={formData.imp}
                onChange={handleInputChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="12.3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Voc (V)</label>
              <input
                type="text"
                name="voc"
                value={formData.voc}
                onChange={handleInputChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="40.1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Isc (A)</label>
              <input
                type="text"
                name="isc"
                value={formData.isc}
                onChange={handleInputChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="13.2"
              />
            </div>
          </div>
        </div>

        {/* Physical Specifications */}
        <div className="bg-green-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Physical Specifications</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions</label>
              <input
                type="text"
                name="dimensions"
                value={formData.dimensions}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2008 x 1002 x 40 mm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Length (mm)</label>
              <input
                type="text"
                name="length"
                value={formData.length}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2008"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Width (mm)</label>
              <input
                type="text"
                name="width"
                value={formData.width}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1002"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Thickness (mm)</label>
              <input
                type="text"
                name="thickness"
                value={formData.thickness}
                onChange={handleInputChange}
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
              <input
                type="text"
                name="weight"
                value={formData.weight}
                onChange={handleInputChange}
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="20.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Cells</label>
              <input
                type="text"
                name="numberOfCells"
                value={formData.numberOfCells}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="72"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frame Color</label>
              <input
                type="text"
                name="frameColor"
                value={formData.frameColor}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Silver"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cable Length (mm)</label>
              <input
                type="text"
                name="cableLength"
                value={formData.cableLength}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1200"
              />
            </div>
          </div>
        </div>

        {/* Temperature Coefficients */}
        <div className="bg-purple-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Temperature Coefficients</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Temp Coeff Pmax (%/°C)</label>
              <input
                type="text"
                name="tempCoeffPmax"
                value={formData.tempCoeffPmax}
                onChange={handleInputChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="-0.38"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Temp Coeff Voc (%/°C)</label>
              <input
                type="text"
                name="tempCoeffVoc"
                value={formData.tempCoeffVoc}
                onChange={handleInputChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="-0.28"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Temp Coeff Isc (%/°C)</label>
              <input
                type="text"
                name="tempCoeffIsc"
                value={formData.tempCoeffIsc}
                onChange={handleInputChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.048"
              />
            </div>
          </div>
        </div>

        {/* Logistics */}
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Truck className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Logistics & Load Specifications</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Panels per Pallet</label>
              <input
                type="text"
                name="panelsPerPallet"
                value={formData.panelsPerPallet}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="26"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pallets per Truck</label>
              <input
                type="text"
                name="palletsPerTruck"
                value={formData.palletsPerTruck}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="22"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Snow Load (Pa)</label>
              <input
                type="text"
                name="snowLoad"
                value={formData.snowLoad}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="5400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Wind Load (Pa)</label>
              <input
                type="text"
                name="windLoad"
                value={formData.windLoad}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2400"
              />
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Package className="w-5 h-5 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Connector Type</label>
              <input
                type="text"
                name="connectorType"
                value={formData.connectorType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="MC4"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Warranty (years)</label>
              <input
                type="text"
                name="warranty"
                value={formData.warranty}
                onChange={handleInputChange}
                min="1"
                max="50"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="25"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any additional remarks or special notes..."
            />
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-indigo-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Upload className="w-5 h-5 text-indigo-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Documentation</h3>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attach PDF or Image - Datasheet, label etc (Optional)
            </label>
            <input
              type="file"
              accept=".pdf,image/*"
              onChange={handleRegularFileUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {attachment && (
              <div className="mt-2 p-2 bg-white rounded border">
                <p className="text-sm text-gray-600">
                  <strong>Attached:</strong> {attachment.name}
                </p>
                {attachment.type === 'application/pdf' && Object.values(formData).some(val => val !== '' && val !== 0) && (
                  <p className="text-xs text-blue-600 mt-1">
                    ✓ This PDF was used to auto-fill the form fields
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !formData.brand || !formData.model}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Submit Request
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
};


export { RequestPanelForm };
